import { useEffect, useRef } from 'react';
import type { FormErrorState } from '../lib/forms';

export interface FormErrorsProps {
  errors: FormErrorState;
  /** Field path → { label, id } so the summary can link to the offending control. */
  fields?: Record<string, { label: string; id: string }>;
  title?: string;
}

/**
 * Error summary shown above a form after a failed submit. It receives focus so keyboard and
 * screen-reader users land on the list of problems, each linking to its field.
 */
export function FormErrors({ errors, fields = {}, title = 'Please correct the following' }: FormErrorsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const entries = Object.entries(errors.fields);
  const visible = errors.general !== null || entries.length > 0;

  useEffect(() => {
    if (visible) ref.current?.focus();
  }, [visible, errors]);

  if (!visible) return null;
  return (
    <div ref={ref} className="notice notice-danger form-errors" role="alert" tabIndex={-1} aria-labelledby="form-errors-title">
      <strong id="form-errors-title">{entries.length ? title : errors.general}</strong>
      {entries.length > 0 && (
        <ul>
          {errors.general && <li>{errors.general}</li>}
          {entries.map(([path, message]) => {
            const f = fields[path];
            return (
              <li key={path}>
                {f ? (
                  <a href={`#${f.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(f.id)?.focus(); }}>
                    {f.label}: {message}
                  </a>
                ) : (
                  <>
                    {path}: {message}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {errors.requestId && <div className="small">Reference: <code>{errors.requestId}</code></div>}
    </div>
  );
}
